import {
  Fragment,
  useEffect,
  useMemo,
  useState,
  useRef,
  useCallback,
  type RefObject,
} from "react";
import { createFileRoute, useSearch } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { useAction, useConvex, useMutation } from "convex/react";
import {
  ChevronDown,
  ChevronRight,
  Ellipsis,
  RotateCcw,
  X,
} from "lucide-react";
import { toast } from "sonner";
import {
  useJobStatus,
  useComputedValues,
  useResolvedViewerSources,
  useTakeoffSelectionRecount,
} from "@/hooks/use-takeoff-pilot";
import {
  useScheduleOverrides,
  useUpsertScheduleOverride,
  useAddScheduleOverrideColumn,
  useRemoveScheduleOverrideCells,
  useResetScheduleOverride,
} from "@/hooks/useScheduleOverrides";
import {
  LoadingState,
  SpinnerState,
  ErrorState,
  CopyingRevisionState,
  ExtractingState,
  DetailsErrorState,
  CountsErrorState,
} from "@/components/page-states";
import { RowDetailsDialog } from "@/components/row-details-dialog";
import { ResizableBottomPanel } from "@/features/takeoff-pilot/resizable-bottom-panel";
import { PanelHeader } from "@/features/takeoff-pilot/panel-header";
import type { DocumentTagSearchResponse } from "@/features/takeoff-pilot/search-document-tags-dialog";
import { FloatingPanelToggle } from "@/features/takeoff-pilot/floating-panel-toggle";

import { api } from "@constructionautomation/backend";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import type {
  CountRow,
  LinkedSchedulePreviewSummary,
  MeasurementBreakdownTargetRef,
  ScheduleOverride,
  TakeoffLinkedSchedulePreviewResponse,
  TagRecord,
  ViewerSource,
} from "@/types/takeoff-pilot";
import { useAppNavigate } from "@/lib/use-app-navigate";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { MultiSelect, type MultiSelectSection } from "@/components/ui/multi-select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  TakeoffResultsViewer,
  type TakeoffResultsViewerHandle,
} from "@/features/takeoff-pilot/takeoff-results-viewer";
import { TagInstanceNavigationButton } from "@/features/takeoff-pilot/tag-instance-navigation-button";
import {
  UnitCostInput,
  ScheduleCellInput,
  CustomLineItemNameInput,
  TagColorInput,
} from "@/features/takeoff-pilot/tag-results-table-inputs";
import { useRouteParam } from "@/lib/use-route-param";
import { ActionLogProvider, useLoggedMutation } from "@/hooks/use-action-log-session";
import {
  GLOBAL_PROCESSING_COMPLETED,
  GLOBAL_PROCESSING_FAILED,
  GLOBAL_PROCESSING_PENDING,
  GLOBAL_PROCESSING_PROCESSING,
  toProcessingStateWithIdle,
  type ProcessingStateWithIdle,
} from "@/lib/processing-states";
import {
  getTotalCost,
  formatCurrency,
  formatMeasurementTotal,
  groupMeasurementBreakdownByDisplayValue,
  downloadTakeoffWorkbook,
} from "@/features/takeoff-pilot/takeoff-utils";
import { MEASUREMENT_TYPES } from "@/lib/measurement-domain";
import type { TakeoffCostBasis } from "@/lib/takeoff-domain";
import type { ScheduleColumnOption } from "@/types/takeoff-pilot";

export const Route = createFileRoute("/takeoffPilot/$jobId")({
  component: TakeoffPilotJobPage,
});

const COST_COLUMNS_VISIBLE_STORAGE_KEY = "takeoff-cost-columns-visible";
const RESULT_QUANTITY_COLUMNS = ["count", "lf", "sqft", "cf"] as const;
const RESULT_TABLE_COLUMN_WIDTHS_REM = {
  actions: 2,
  color: 2.25,
  tag: 18,
  schedule: 11,
  quantity: 4,
  unitCost: 7.5,
  totalCost: 6,
} as const;

type ResultQuantityColumn = (typeof RESULT_QUANTITY_COLUMNS)[number];
const PDF_EXPORT_MEASUREMENT_COLUMNS = ["Count", "LF", "SQFT", "CF"] as const;
const PDF_EXPORT_SCHEDULE_COLUMN_PREFIX = "schedule:";

function encodePdfExportScheduleColumn(column: string) {
  return `${PDF_EXPORT_SCHEDULE_COLUMN_PREFIX}${column}`;
}

function mergeScheduleColumns(args: {
  linkedColumns: ScheduleColumnOption[];
  overrideHeaders: string[];
}) {
  const columns = new Map<string, ScheduleColumnOption>(
    args.linkedColumns.map((column) => [column.value, column] as const)
  );

  for (const header of args.overrideHeaders) {
    if (!columns.has(header)) {
      columns.set(header, {
        value: header,
        label: header,
        isCustom: true,
      });
    }
  }

  return Array.from(columns.values());
}

function TagResultsTable({
  tags,
  hasSchedule,
  selectedResults,
  selectedScheduleHeaders,
  customScheduleHeaders,
  visibleQuantityColumns,
  costColumnsVisible,
  linkedPreviewsByTagId,
  scheduleOverridesByTagId,
  onUpsertScheduleOverride,
  onRemoveScheduleOverrideCells,
  onResetScheduleOverrides,
  expandedBreakdownTagRowIds,
  navigableTagIds,
  onFocusPreviousInstance,
  onFocusNextInstance,
  onFocusMeasurementBreakdownRow,
  onToggleExpandedBreakdownTagRow,
  onOpenScheduleRow,
  isCounting,
  setIncluded,
  removeCustomTag,
  renameCustomTag,
  setUnitCost,
  setCostBasis,
  setColorHex,
}: {
  tags: TagRecord[];
  hasSchedule: boolean;
  selectedResults: CountRow[];
  selectedScheduleHeaders: string[];
  customScheduleHeaders: Set<string>;
  visibleQuantityColumns: Set<ResultQuantityColumn>;
  costColumnsVisible: boolean;
  linkedPreviewsByTagId: Map<string, LinkedSchedulePreviewSummary>;
  scheduleOverridesByTagId: Map<string, ScheduleOverride>;
  onUpsertScheduleOverride: (args: {
    tagId: Id<"takeoffPilotExtractedTags">;
    cells: Record<string, string>;
  }) => Promise<unknown>;
  onRemoveScheduleOverrideCells: (args: {
    tagId: Id<"takeoffPilotExtractedTags">;
    keys: string[];
  }) => Promise<unknown>;
  onResetScheduleOverrides: (args: {
    tagId: Id<"takeoffPilotExtractedTags">;
  }) => Promise<unknown>;
  expandedBreakdownTagRowIds: Set<string>;
  navigableTagIds: Set<string>;
  onFocusPreviousInstance: (tagId: Id<"takeoffPilotExtractedTags">) => void;
  onFocusNextInstance: (tagId: Id<"takeoffPilotExtractedTags">) => void;
  onFocusMeasurementBreakdownRow: (args: {
    rowKey: string;
    direction: "previous" | "next";
    targets: MeasurementBreakdownTargetRef[];
  }) => void;
  onToggleExpandedBreakdownTagRow: (tagId: string) => void;
  onOpenScheduleRow: (args: {
    tagRawValue: string;
    matchedRow: LinkedSchedulePreviewSummary["matchedRows"][number];
  }) => void;
  isCounting: boolean;
  setIncluded: (args: { tagId: Id<"takeoffPilotExtractedTags">; included: boolean }) => Promise<unknown>;
  removeCustomTag: (args: { tagId: Id<"takeoffPilotExtractedTags"> }) => Promise<unknown>;
  renameCustomTag: (args: {
    tagId: Id<"takeoffPilotExtractedTags">;
    rawValue: string;
  }) => Promise<unknown>;
  setUnitCost: (args: { tagId: Id<"takeoffPilotExtractedTags">; unitCost: number | null }) => Promise<unknown>;
  setCostBasis: (args: {
    tagId: Id<"takeoffPilotExtractedTags">;
    costBasis: TakeoffCostBasis;
  }) => Promise<unknown>;
  setColorHex: (args: {
    tagId: Id<"takeoffPilotExtractedTags">;
    colorHex: string;
  }) => Promise<unknown>;
}) {
  const resultsByTagId = useMemo(() => {
    const map = new Map<string, CountRow>();
    for (const result of selectedResults) {
      map.set(result.tagId, result);
    }
    return map;
  }, [selectedResults]);
  const selectedTags = tags.filter((tag) => tag.included);

  const handleRemove = async (tag: TagRecord) => {
    try {
      if (tag.isCustom ?? false) {
        await removeCustomTag({ tagId: tag._id });
        return;
      }

      await setIncluded({ tagId: tag._id, included: false });
    } catch (error) {
      toast.error(tag.isCustom ?? false ? "Failed to delete custom line item" : "Failed to remove tag");
      console.error(error);
    }
  };

  if (selectedTags.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-sm text-muted-foreground">
        <p>
          {hasSchedule
            ? "No tags selected. Use the dropdown above to add tags from the schedule."
            : "No custom items yet. Use Add Custom Line Item to begin."}
        </p>
      </div>
    );
  }

  const tableMinWidthRem =
    RESULT_TABLE_COLUMN_WIDTHS_REM.actions +
    RESULT_TABLE_COLUMN_WIDTHS_REM.color +
    RESULT_TABLE_COLUMN_WIDTHS_REM.tag +
    selectedScheduleHeaders.length * RESULT_TABLE_COLUMN_WIDTHS_REM.schedule +
    visibleQuantityColumns.size * RESULT_TABLE_COLUMN_WIDTHS_REM.quantity +
    (costColumnsVisible
      ? RESULT_TABLE_COLUMN_WIDTHS_REM.unitCost + RESULT_TABLE_COLUMN_WIDTHS_REM.totalCost
      : 0);

  return (
    <div className="flex h-full flex-col">
      {/* Table with sticky header */}
      <div className="flex-1 overflow-auto">
        <Table
          className="table-fixed min-w-full"
          style={{ minWidth: `${tableMinWidthRem}rem` }}
        >
          <colgroup>
            <col style={{ width: `${RESULT_TABLE_COLUMN_WIDTHS_REM.actions}rem` }} />
            <col style={{ width: `${RESULT_TABLE_COLUMN_WIDTHS_REM.color}rem` }} />
            <col style={{ width: `${RESULT_TABLE_COLUMN_WIDTHS_REM.tag}rem` }} />
            {selectedScheduleHeaders.map((header) => (
              <col
                key={`schedule:${header}`}
                style={{ width: `${RESULT_TABLE_COLUMN_WIDTHS_REM.schedule}rem` }}
              />
            ))}
            {Array.from(visibleQuantityColumns).map((column) => (
              <col
                key={`quantity:${column}`}
                style={{ width: `${RESULT_TABLE_COLUMN_WIDTHS_REM.quantity}rem` }}
              />
            ))}
            {costColumnsVisible ? (
              <>
                <col style={{ width: `${RESULT_TABLE_COLUMN_WIDTHS_REM.unitCost}rem` }} />
                <col style={{ width: `${RESULT_TABLE_COLUMN_WIDTHS_REM.totalCost}rem` }} />
              </>
            ) : null}
          </colgroup>
          <TableHeader className="sticky top-0 z-10 bg-background">
            <TableRow>
              <TableHead className="h-8 px-1.5 py-1"></TableHead>
              <TableHead className="h-8 px-1.5 py-1"></TableHead>
              <TableHead className="h-8 px-1.5 py-1 text-[10px] tracking-[0.12em]">Tag</TableHead>
              {selectedScheduleHeaders.map((header) => (
                <TableHead
                  key={header}
                  className="h-8 px-1.5 py-1 text-[9px] leading-tight whitespace-normal break-words tracking-[0.08em]"
                  title={header}
                >
                  {header}
                </TableHead>
              ))}
              {visibleQuantityColumns.has("count") ? (
                <TableHead className="h-8 px-1.5 py-1 text-right font-mono text-[10px] tracking-[0.1em]">Count</TableHead>
              ) : null}
              {visibleQuantityColumns.has("lf") ? (
                <TableHead className="h-8 px-1.5 py-1 text-right font-mono text-[10px] tracking-[0.1em]">LF</TableHead>
              ) : null}
              {visibleQuantityColumns.has("sqft") ? (
                <TableHead className="h-8 px-1.5 py-1 text-right font-mono text-[10px] tracking-[0.1em]">SQFT</TableHead>
              ) : null}
              {visibleQuantityColumns.has("cf") ? (
                <TableHead className="h-8 px-1.5 py-1 text-right font-mono text-[10px] tracking-[0.1em]">CF</TableHead>
              ) : null}
              {costColumnsVisible ? (
                <>
                  <TableHead className="h-8 px-1.5 py-1 text-right font-mono text-[10px] tracking-[0.1em]">Unit $</TableHead>
                  <TableHead className="h-8 px-1.5 py-1 text-right font-mono text-[10px] tracking-[0.1em]">Total $</TableHead>
                </>
              ) : null}
            </TableRow>
          </TableHeader>
          <TableBody>
            {selectedTags.map((tag) => {
              const result = resultsByTagId.get(tag._id);
              const linkedPreview = linkedPreviewsByTagId.get(String(tag._id));
              const primaryMatchedRow = linkedPreview?.matchedRows[0] ?? null;
              const isExpanded = expandedBreakdownTagRowIds.has(String(tag._id));
              const measurementBreakdownRows = groupMeasurementBreakdownByDisplayValue(
                result?.breakdown,
                result?.breakdownTargets
              );
              const hasBreakdownDetails = measurementBreakdownRows.length > 0;
              const totalCostForTag = getTotalCost(result, tag.unitCost, tag.costBasis);
              const canNavigateInstances = navigableTagIds.has(String(tag._id));
              const showTagNavigation = !(tag.isCustom ?? false) || canNavigateInstances;
              const rowAccentColor = tag.colorHex ?? result?.colorHex ?? "#0EA5E9";

              return (
                <Fragment key={tag._id}>
                  <TableRow
                    className="h-9"
                    style={{ boxShadow: `inset 2px 0 0 ${rowAccentColor}` }}
                  >
                    <TableCell className="px-1.5 py-0 align-middle">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button
                            disabled={isCounting}
                            className="rounded-sm border border-transparent p-1 opacity-60 transition-opacity hover:border-[#333] hover:bg-muted hover:opacity-100 disabled:opacity-30"
                            aria-label={`Actions for ${tag.rawValue}`}
                          >
                            <Ellipsis className="h-4 w-4" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" className="z-[160]">
                          <DropdownMenuItem
                            onClick={() => void handleRemove(tag)}
                            className="text-destructive focus:text-destructive"
                          >
                            <X className="mr-2 h-4 w-4" />
                            {tag.isCustom ?? false ? "Delete" : "Remove"}
                          </DropdownMenuItem>
                          {scheduleOverridesByTagId.has(String(tag._id)) ? (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => void onResetScheduleOverrides({ tagId: tag._id }).catch((error) => {
                                  toast.error("Failed to reset schedule data");
                                  console.error(error);
                                })}
                              >
                                <RotateCcw className="mr-2 h-4 w-4" />
                                Reset schedule data
                              </DropdownMenuItem>
                            </>
                          ) : null}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                    <TableCell className="px-1.5 py-0 align-middle">
                      <div className="flex items-center justify-center">
                        <TagColorInput
                          tag={tag}
                          resolvedColorHex={tag.colorHex ?? result?.colorHex ?? "#0EA5E9"}
                          disabled={isCounting}
                          setColorHex={setColorHex}
                        />
                      </div>
                    </TableCell>
                    <TableCell className="px-1.5 py-0.5 align-middle">
                      <div className="flex min-w-0 items-center gap-1.5">
                        {hasBreakdownDetails ? (
                          <button
                            type="button"
                            onClick={() => onToggleExpandedBreakdownTagRow(String(tag._id))}
                            className="inline-flex size-6 items-center justify-center rounded-sm border border-[#333] bg-background/80 text-muted-foreground transition-colors hover:bg-muted/70 hover:text-foreground"
                            aria-label={`Toggle measurement breakdown for ${tag.rawValue}`}
                            title={isExpanded ? "Hide measurement breakdown" : "Show measurement breakdown"}
                          >
                            {isExpanded ? (
                              <ChevronDown className="size-3.5" />
                            ) : (
                              <ChevronRight className="size-3.5" />
                            )}
                          </button>
                        ) : (
                          <span className="size-6" aria-hidden="true" />
                        )}
                        <div className="min-w-0 flex-1">
                          {tag.isCustom ?? false ? (
                            <CustomLineItemNameInput
                              tag={tag}
                              disabled={isCounting}
                              renameCustomTag={renameCustomTag}
                            />
                          ) : primaryMatchedRow ? (
                            <button
                              type="button"
                              onClick={() =>
                                onOpenScheduleRow({
                                  tagRawValue: tag.rawValue,
                                  matchedRow: primaryMatchedRow,
                                })
                              }
                              className="block max-w-full truncate text-left text-[11px] font-medium hover:underline"
                              title={tag.rawValue}
                            >
                              {tag.rawValue}
                            </button>
                          ) : (
                            <span className="block truncate text-[11px] font-medium" title={tag.rawValue}>
                              {tag.rawValue}
                            </span>
                          )}
                        </div>
                        {showTagNavigation ? (
                          <div className="flex w-10 shrink-0 items-center justify-end gap-px">
                            <TagInstanceNavigationButton
                              direction="previous"
                              tagLabel={tag.rawValue}
                              disabled={!canNavigateInstances}
                              onClick={() => onFocusPreviousInstance(tag._id)}
                            />
                            <TagInstanceNavigationButton
                              direction="next"
                              tagLabel={tag.rawValue}
                              disabled={!canNavigateInstances}
                              onClick={() => onFocusNextInstance(tag._id)}
                            />
                          </div>
                        ) : null}
                      </div>
                    </TableCell>
                    {selectedScheduleHeaders.map((header) => {
                      const override = scheduleOverridesByTagId.get(String(tag._id));
                      const overrideValue = override?.cells[header];
                      const baseValue = primaryMatchedRow?.cells[header] ?? "";
                      const isOverridden = overrideValue !== undefined;
                      return (
                        <TableCell
                          key={`${tag._id}:${header}`}
                          className="min-w-0 px-1.5 py-0.5 align-top text-[9px]"
                        >
                          <ScheduleCellInput
                            tagId={tag._id}
                            header={header}
                            baseValue={baseValue}
                            overrideValue={overrideValue}
                            isOverridden={isOverridden}
                            preserveEmptyOverride={customScheduleHeaders.has(header)}
                            onUpsert={onUpsertScheduleOverride}
                            onRemoveCells={onRemoveScheduleOverrideCells}
                          />
                        </TableCell>
                      );
                    })}
                    {visibleQuantityColumns.has("count") ? (
                      <TableCell className="px-1.5 py-0.5 text-right font-mono text-[10px] tabular-nums">
                        {result ? result.count : "—"}
                      </TableCell>
                    ) : null}
                    {visibleQuantityColumns.has("lf") ? (
                      <TableCell className="px-1.5 py-0.5 text-right font-mono text-[10px] tabular-nums">
                        {formatMeasurementTotal(MEASUREMENT_TYPES.LF, result?.lf)}
                      </TableCell>
                    ) : null}
                    {visibleQuantityColumns.has("sqft") ? (
                      <TableCell className="px-1.5 py-0.5 text-right font-mono text-[10px] tabular-nums">
                        {formatMeasurementTotal(MEASUREMENT_TYPES.SQFT, result?.sqft)}
                      </TableCell>
                    ) : null}
                    {visibleQuantityColumns.has("cf") ? (
                      <TableCell className="px-1.5 py-0.5 text-right font-mono text-[10px] tabular-nums">
                        {formatMeasurementTotal(MEASUREMENT_TYPES.CF, result?.cf)}
                      </TableCell>
                    ) : null}
                    {costColumnsVisible ? (
                      <>
                        <TableCell className="px-1.5 py-0.5 text-right">
                          <UnitCostInput
                            tag={tag}
                            disabled={isCounting}
                            setUnitCost={setUnitCost}
                            setCostBasis={setCostBasis}
                          />
                        </TableCell>
                        <TableCell className="px-1.5 py-0.5 text-right font-mono text-[10px] font-medium tabular-nums">
                          {totalCostForTag === null ? "—" : `$${formatCurrency(totalCostForTag)}`}
                        </TableCell>
                      </>
                    ) : null}
                  </TableRow>
                  {hasBreakdownDetails && isExpanded
                    ? [
                        ...measurementBreakdownRows.map((row) => {
                          const rowKey = `${tag._id}:measurement:${row.measurementType}:${row.displayValue}`;
                          const canNavigateMeasurement = row.targets.length > 0;
                          return (
                            <TableRow
                              key={rowKey}
                              className="h-8 bg-muted/20"
                              style={{ boxShadow: `inset 2px 0 0 ${rowAccentColor}` }}
                            >
                              <TableCell className="px-1.5 py-0 align-middle" />
                              <TableCell className="px-1.5 py-0 align-middle" />
                              <TableCell className="px-1.5 py-0.5 align-middle text-[9px] text-muted-foreground">
                                <div className="grid min-w-0 grid-cols-[1.5rem_minmax(0,1fr)_2.5rem] items-center gap-1.5">
                                  <span className="size-6" aria-hidden="true" />
                                  <span className="truncate font-mono text-[10px] text-foreground tabular-nums">
                                    {row.displayValue} {row.measurementType}
                                  </span>
                                  <div className="flex items-center gap-px">
                                    <TagInstanceNavigationButton
                                      direction="previous"
                                      tagLabel={`${tag.rawValue} ${row.displayValue} ${row.measurementType}`}
                                      disabled={!canNavigateMeasurement}
                                      onClick={() => {
                                        if (!canNavigateMeasurement) {
                                          return;
                                        }
                                        onFocusMeasurementBreakdownRow({
                                          rowKey,
                                          direction: "previous",
                                          targets: row.targets,
                                        });
                                      }}
                                    />
                                    <TagInstanceNavigationButton
                                      direction="next"
                                      tagLabel={`${tag.rawValue} ${row.displayValue} ${row.measurementType}`}
                                      disabled={!canNavigateMeasurement}
                                      onClick={() => {
                                        if (!canNavigateMeasurement) {
                                          return;
                                        }
                                        onFocusMeasurementBreakdownRow({
                                          rowKey,
                                          direction: "next",
                                          targets: row.targets,
                                        });
                                      }}
                                    />
                                  </div>
                                  <span aria-hidden="true" />
                                </div>
                              </TableCell>
                              {selectedScheduleHeaders.map((header) => (
                                <TableCell
                                  key={`${tag._id}:measurement:${row.measurementType}:${row.displayValue}:${header}`}
                                  className="px-1.5 py-0.5"
                                />
                              ))}
                              {visibleQuantityColumns.has("count") ? (
                                <TableCell className="px-1.5 py-0.5 text-right font-mono text-[10px] tabular-nums">
                                  {row.count}
                                </TableCell>
                              ) : null}
                              {visibleQuantityColumns.has("lf") ? (
                                <TableCell className="px-1.5 py-0.5" />
                              ) : null}
                              {visibleQuantityColumns.has("sqft") ? (
                                <TableCell className="px-1.5 py-0.5" />
                              ) : null}
                              {visibleQuantityColumns.has("cf") ? (
                                <TableCell className="px-1.5 py-0.5" />
                              ) : null}
                              {costColumnsVisible ? (
                                <>
                                  <TableCell className="px-1.5 py-0.5" />
                                  <TableCell className="px-1.5 py-0.5" />
                                </>
                              ) : null}
                            </TableRow>
                          );
                        }),
                      ]
                    : null}
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function useTakeoffLinkedSchedulePreview(
  jobId: Id<"takeoffPilotJobs"> | undefined,
  overrideHeaders: string[],
  enabled: boolean
) {
  const [selectedScheduleHeaders, setSelectedScheduleHeaders] = useState<string[]>([]);
  const getLinkedSchedulePreview = useAction(
    api.takeoffPilot.tagPreview.getLinkedSchedulePreview
  );
  const { data: linkedSchedulePreviewData } = useQuery(
    jobId && enabled
      ? {
          queryKey: ["takeoff-linked-schedule-preview", jobId],
          queryFn: async () =>
            await getLinkedSchedulePreview({ jobId }),
          staleTime: 30_000,
          refetchOnWindowFocus: false,
        }
      : {
          queryKey: ["takeoff-linked-schedule-preview", "skip"],
          queryFn: async () => undefined,
          enabled: false,
        }
  );

  const availableScheduleColumns = useMemo(
    () =>
      mergeScheduleColumns({
        linkedColumns: linkedSchedulePreviewData?.availableColumns ?? [],
        overrideHeaders,
      }),
    [linkedSchedulePreviewData?.availableColumns, overrideHeaders]
  );

  useEffect(() => {
    const availableHeaders = new Set(availableScheduleColumns.map((column) => column.value));
    setSelectedScheduleHeaders((previous) => {
      const next = previous.filter((header) => availableHeaders.has(header));
      return next.length === previous.length ? previous : next;
    });
  }, [availableScheduleColumns]);

  const linkedSchedulePreview =
    linkedSchedulePreviewData as TakeoffLinkedSchedulePreviewResponse | undefined;
  const linkedPreviewsByTagId = useMemo(
    () =>
      new Map(
        (linkedSchedulePreview?.linkedPreviews ?? []).map((preview) => [
          String(preview.tagId),
          preview,
        ] as const)
      ),
    [linkedSchedulePreview]
  );

  const handleSelectScheduleHeader = useCallback((header: string) => {
    setSelectedScheduleHeaders((previous) =>
      previous.includes(header) ? previous : [...previous, header]
    );
  }, []);

  const handleRemoveScheduleHeader = useCallback((header: string) => {
    setSelectedScheduleHeaders((previous) =>
      previous.filter((candidate) => candidate !== header)
    );
  }, []);

  return {
    linkedSchedulePreview,
    availableScheduleColumns,
    linkedPreviewsByTagId,
    selectedScheduleHeaders,
    handleSelectScheduleHeader,
    handleRemoveScheduleHeader,
  };
}

function renderTakeoffPageState(args: {
  jobId: Id<"takeoffPilotJobs"> | undefined;
  detailsError: unknown;
  isPending: boolean;
  details:
    | {
        job: {
          status: string;
          error?: string;
        };
      }
    | undefined;
  status: ReturnType<typeof useJobStatus>;
  countsError: unknown;
  hasMeasurementTotalsError: boolean;
  measurementTotalsError: unknown;
}) {
  if (!args.jobId) return <LoadingState />;
  if (args.detailsError) return <DetailsErrorState />;
  if (args.isPending) return <SpinnerState />;
  if (!args.details) return <LoadingState />;
  if (args.status.isCopyingRevision) return <CopyingRevisionState />;
  if (args.status.isExtracting) return <ExtractingState />;
  if (args.details.job.error) {
    return (
      <ErrorState
        title="Tag Counting Failed"
        message={args.details.job.error}
        destructive
      />
    );
  }
  if (args.countsError) return <CountsErrorState />;
  if (args.hasMeasurementTotalsError) {
    return (
      <ErrorState
        title="Measurement totals failed"
        message={
          args.measurementTotalsError instanceof Error
            ? args.measurementTotalsError.message
            : "Unable to load measurement totals"
        }
        destructive
      />
    );
  }
  return null;
}

function renderTakeoffResultsViewer(args: {
  activeSource: ViewerSource | undefined;
  jobId: Id<"takeoffPilotJobs">;
  viewerRef: RefObject<TakeoffResultsViewerHandle | null>;
  viewerTags: ReturnType<typeof useComputedValues>["viewerTags"];
  onNavigableTagIdsChange: (tagIds: string[]) => void;
  viewerSources: ViewerSource[];
  selectedPlanDocumentId: string;
  onSelectPlanDocumentId: (value: string) => void;
  onClose: () => void;
  onDownloadWorkbook: () => void;
  onExportPdf: () => void;
  onDownloadPdf: () => void;
  exportStatus: ProcessingStateWithIdle;
  exportError?: string;
  isPdfExportInProgress: boolean;
  canExportPdf: boolean;
  canDownloadPdf: boolean;
  canDownload: boolean;
  bottomPanelHeight: number;
}) {
  if (!args.activeSource?.fileUrl) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
        The selected plan document will appear after it becomes available.
      </div>
    );
  }

  return (
    <TakeoffResultsViewer
      ref={args.viewerRef}
      jobId={args.jobId}
      planDocumentId={args.activeSource.planDocumentId}
      planRevisionId={args.activeSource.revisionId}
      planFilename={args.activeSource.filename}
      planFileUrl={args.activeSource.fileUrl}
      planProjectId={args.activeSource.projectId}
      planSourceDocumentId={args.activeSource.documentId}
      documentIntelligenceRunId={args.activeSource.documentIntelligenceRunId}
      tags={args.viewerTags}
      isViewOnly={args.activeSource.sourceType === "schedule"}
      onNavigableTagIdsChange={args.onNavigableTagIdsChange}
      viewerSources={args.viewerSources}
      selectedPlanDocumentId={args.selectedPlanDocumentId}
      onSelectPlanDocumentId={args.onSelectPlanDocumentId}
      onClose={args.onClose}
      onDownloadWorkbook={args.onDownloadWorkbook}
      onExportPdf={args.onExportPdf}
      onDownloadPdf={args.onDownloadPdf}
      exportStatus={args.exportStatus}
      exportError={args.exportError}
      isPdfExportInProgress={args.isPdfExportInProgress}
      canExportPdf={args.canExportPdf}
      canDownloadPdf={args.canDownloadPdf}
      canDownload={args.canDownload}
      bottomPanelHeight={args.bottomPanelHeight}
    />
  );
}

export function TakeoffPilotJobPage() {
  const jobId = useRouteParam("jobId") as Id<"takeoffPilotJobs"> | undefined;
  const { data: details } = useQuery(
    convexQuery(api.takeoffPilot.jobs.getWorkspaceDetails, jobId ? { jobId } : "skip")
  );

  return (
    <ActionLogProvider
      projectId={details?.job.projectId ?? null}
      pilot="TakeoffPilot"
      targetKind="takeoffPilotJob"
      targetId={jobId}
    >
      <TakeoffPilotJobPageContent />
    </ActionLogProvider>
  );
}

// eslint-disable-next-line complexity
function TakeoffPilotJobPageContent() {
  const navigate = useAppNavigate();
  const jobId = useRouteParam("jobId") as Id<"takeoffPilotJobs"> | undefined;
  const returnToRevPilotComparisonIdFromParams = useRouteParam("returnToRevPilotComparisonId");
  const routeSearch = useSearch({ strict: false }) as Record<string, string | undefined>;
  const returnToRevPilotComparisonId =
    returnToRevPilotComparisonIdFromParams ?? routeSearch.returnToRevPilotComparisonId;
  const queuePlanAnalysis = useMutation(api.takeoffPilot.jobs.queuePlanAnalysis);
  const convex = useConvex();
  const setIncluded = useLoggedMutation(api.takeoffPilot.tags.setIncluded);
  const setIncludedBatch = useLoggedMutation(api.takeoffPilot.tags.setIncludedBatch);
  const createCustomTag = useLoggedMutation(api.takeoffPilot.tags.createCustom);
  const createCustomTagsFromDocumentTags = useLoggedMutation(api.takeoffPilot.tags.createCustomFromDocumentTags);
  const removeCustomTag = useLoggedMutation(api.takeoffPilot.tags.removeCustom);
  const renameCustomTag = useLoggedMutation(api.takeoffPilot.tags.renameCustom);
  const setUnitCost = useLoggedMutation(api.takeoffPilot.tags.setUnitCost);
  const setCostBasis = useLoggedMutation(api.takeoffPilot.tags.setCostBasis);
  const setTagColorHex = useLoggedMutation(api.takeoffPilot.tags.setColorHex);
  const generatePdfExport = useMutation(api.takeoffPilot.jobs.generatePdfExport);
  const { overrides: scheduleOverrides } = useScheduleOverrides(jobId);
  const upsertScheduleOverride = useUpsertScheduleOverride();
  const addScheduleOverrideColumn = useAddScheduleOverrideColumn();
  const removeScheduleOverrideCells = useRemoveScheduleOverrideCells();
  const resetScheduleOverride = useResetScheduleOverride();
  const scheduleOverridesByTagId = useMemo(
    () =>
      new Map(
        scheduleOverrides.map((override) => [String(override.tagId), override] as const)
      ),
    [scheduleOverrides]
  );
  const overrideHeaders = useMemo(
    () =>
      Array.from(
        new Set(scheduleOverrides.flatMap((override) => Object.keys(override.cells)))
      ).sort((left, right) => left.localeCompare(right)),
    [scheduleOverrides]
  );
  const [optimisticScheduleHeaders, setOptimisticScheduleHeaders] = useState<string[]>([]);
  const [selectedPlanDocumentId, setSelectedPlanDocumentId] = useState<string>("");
  const [includeScheduleInPdf, setIncludeScheduleInPdf] = useState(true);
  const [includeTagsInPdf, setIncludeTagsInPdf] = useState(true);
  const [includeMeasurementsInPdf, setIncludeMeasurementsInPdf] = useState(true);
  const [pdfReportMode, setPdfReportMode] = useState<"executive_summary" | "full">("full");
  const [pdfExportIncludeColumns, setPdfExportIncludeColumns] = useState<string[]>([
    ...PDF_EXPORT_MEASUREMENT_COLUMNS,
  ]);
  const [isPdfExportDialogOpen, setIsPdfExportDialogOpen] = useState(false);
  const [isQueueingPdfExport, setIsQueueingPdfExport] = useState(false);
  const [isPdfExportOptimisticPending, setIsPdfExportOptimisticPending] = useState(false);
  const [isPanelOpen, setIsPanelOpen] = useState(true);
  const [visibleQuantityColumns, setVisibleQuantityColumns] =
    useState<ResultQuantityColumn[]>([...RESULT_QUANTITY_COLUMNS]);
  const [panelHeight, setPanelHeight] = useState(300);
  const [innerHeight, setInnerHeight] = useState(() => typeof window !== "undefined" ? window.innerHeight : 800);
  const [selectedScheduleRow, setSelectedScheduleRow] = useState<{
    tagRawValue: string;
    matchedRow: LinkedSchedulePreviewSummary["matchedRows"][number];
  } | null>(null);
  const [expandedBreakdownTagRowIds, setExpandedBreakdownTagRowIds] = useState<Set<string>>(
    new Set()
  );
  const takeoffResultsViewerRef = useRef<TakeoffResultsViewerHandle | null>(null);
  const pdfExportJobContextRef = useRef<{ jobId: string; hasSchedule: boolean } | null>(null);
  const [navigableTagIds, setNavigableTagIds] = useState<Set<string>>(new Set());
  const [costColumnsVisible, setCostColumnsVisible] = useState(() => {
    if (typeof window === "undefined") {
      return true;
    }

    try {
      const stored = window.localStorage.getItem(COST_COLUMNS_VISIBLE_STORAGE_KEY);
      if (stored === "false") {
        return false;
      }
      if (stored === "true") {
        return true;
      }
      return true;
    } catch {
      return true;
    }
  });

  const [pendingSelections, setPendingSelections] = useState<Map<string, boolean>>(new Map());
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const flushInFlightRef = useRef<Promise<boolean> | null>(null);

  useEffect(() => {
    const handleResize = () => {
      const newHeight = window.innerHeight;
      setInnerHeight(newHeight);
      const newMaxHeight = newHeight - 100;
      setPanelHeight((prev) => Math.min(prev, newMaxHeight));
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const maxHeight = innerHeight - 100;
    setPanelHeight((prev) => Math.min(prev, maxHeight));
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    try {
      window.localStorage.setItem(
        COST_COLUMNS_VISIBLE_STORAGE_KEY,
        String(costColumnsVisible)
      );
    } catch {
      // Ignore storage write failures and keep the in-memory preference.
    }
  }, [costColumnsVisible]);

  const handleDropdownSelect = (tagId: string) => {
    setPendingSelections((prev) => {
      const next = new Map(prev);
      next.set(tagId, true);
      return next;
    });
  };

  const handleDropdownRemove = (tagId: string) => {
    setPendingSelections((prev) => {
      const next = new Map(prev);
      next.set(tagId, false);
      return next;
    });
  };
  const handleSelectQuantityColumn = useCallback((column: string) => {
    if (!RESULT_QUANTITY_COLUMNS.includes(column as ResultQuantityColumn)) {
      return;
    }

    setVisibleQuantityColumns((previous) =>
      previous.includes(column as ResultQuantityColumn)
        ? previous
        : [...previous, column as ResultQuantityColumn]
    );
  }, []);

  const handleRemoveQuantityColumn = useCallback((column: string) => {
    setVisibleQuantityColumns((previous) =>
      previous.filter((candidate) => candidate !== column)
    );
  }, []);

  const flushPendingSelections = useCallback(async () => {
    if (flushInFlightRef.current) {
      return await flushInFlightRef.current;
    }

    if (pendingSelections.size === 0) {
      return true;
    }

    const sentSelections = new Map(pendingSelections);
    const selections = Array.from(sentSelections.entries()).map(([tagId, included]) => ({
      tagId: tagId as Id<"takeoffPilotExtractedTags">,
      included,
    }));

    let flushPromise: Promise<boolean> | null = null;
    flushPromise = (async () => {
      try {
        await setIncludedBatch({ selections });
        setPendingSelections((prev) => {
          const next = new Map(prev);
          for (const [id, included] of sentSelections) {
            if (next.get(id) === included) {
              next.delete(id);
            }
          }
          return next;
        });
        return true;
      } catch (error) {
        toast.error("Failed to update tag selections");
        console.error(error);
        return false;
      } finally {
        if (flushInFlightRef.current === flushPromise) {
          flushInFlightRef.current = null;
        }
      }
    })();

    flushInFlightRef.current = flushPromise;
    return await flushPromise;
  }, [pendingSelections, setIncludedBatch]);

  const handleDropdownOpenChange = useCallback(async (open: boolean) => {
    setIsDropdownOpen(open);

    if (!open) {
      return await flushPendingSelections();
    }
    return true;
  }, [flushPendingSelections]);

  const handleHideResults = useCallback(async () => {
    const flushed = await handleDropdownOpenChange(false);
    if (flushed === false) {
      return;
    }
    setIsPanelOpen(false);
  }, [handleDropdownOpenChange]);

  const { data: details, isPending, error: detailsError } = useQuery(
    convexQuery(api.takeoffPilot.jobs.getWorkspaceDetails, jobId ? { jobId } : "skip")
  );
  const includedTagIds = useMemo(
    () => (details?.tags ?? []).filter((tag) => tag.included).map((tag) => tag._id),
    [details?.tags]
  );
  const { data: counts, isPending: isCountsPending, error: countsError } = useQuery(
    convexQuery(
      api.takeoffPilot.counts.listSummariesByTag,
      jobId ? { jobId, tagIds: includedTagIds } : "skip"
    )
  );
  const {
    data: measurementTotals,
    isPending: isMeasurementTotalsPending,
    error: measurementTotalsError,
  } = useQuery(
    convexQuery(
      api.takeoffPilot.measurements.listTotalsByTag,
      jobId ? { jobId, tagIds: includedTagIds } : "skip"
    )
  );
  const { data: pdfExportDownloadUrl, refetch: refetchPdfExportDownloadUrl } = useQuery(
    convexQuery(api.takeoffPilot.jobs.getPdfExportDownloadUrl, jobId ? { jobId } : "skip")
  );
  const { data: viewerSources } = useQuery(
    convexQuery(api.takeoffPilot.jobs.listPlanDocumentViewerSources, jobId ? { jobId } : "skip")
  );
  const scheduleHeadersFromOverrides = useMemo(
    () =>
      Array.from(new Set([...overrideHeaders, ...optimisticScheduleHeaders])).sort((left, right) =>
        left.localeCompare(right)
      ),
    [optimisticScheduleHeaders, overrideHeaders]
  );
  const hasSchedule = Boolean(details?.job.scheduleRevisionId);
  const {
    availableScheduleColumns,
    linkedPreviewsByTagId,
    selectedScheduleHeaders,
    handleSelectScheduleHeader,
    handleRemoveScheduleHeader,
  } = useTakeoffLinkedSchedulePreview(jobId, scheduleHeadersFromOverrides, hasSchedule);
  const customScheduleHeaders = useMemo(
    () => new Set(availableScheduleColumns.filter((column) => column.isCustom).map((column) => column.value)),
    [availableScheduleColumns]
  );
  const visibleQuantityColumnSet = useMemo(
    () => new Set(visibleQuantityColumns),
    [visibleQuantityColumns]
  );
  const pdfExportColumnSections = useMemo<MultiSelectSection[]>(
    () => [
      {
        id: "takeoff-columns",
        label: "Takeoff Columns",
        options: PDF_EXPORT_MEASUREMENT_COLUMNS.map((column) => ({
          value: column,
          label: column,
        })),
      },
      {
        id: "schedule-columns",
        label: hasSchedule ? "Schedule Columns" : "Custom Columns",
        options: availableScheduleColumns.map((column) => ({
          value: encodePdfExportScheduleColumn(column.value),
          label: column.label,
        })),
      },
    ].filter((section) => section.options.length > 0),
    [availableScheduleColumns, hasSchedule]
  );
  const availablePdfExportColumns = useMemo(
    () => new Set(pdfExportColumnSections.flatMap((section) => section.options.map((option) => option.value))),
    [pdfExportColumnSections]
  );

  const tags = details?.tags ?? [];
  const tables = details?.tables ?? [];
  const countRows = counts as CountRow[] | undefined;
  const isMeasurementTotalsInitialLoad = isMeasurementTotalsPending && !measurementTotals;
  const hasMeasurementTotalsError = !measurementTotals && !!measurementTotalsError;

  const computed = useComputedValues(
    tags,
    countRows,
    measurementTotals,
    viewerSources as ViewerSource[] | undefined
  );
  const resolvedViewerSources = useResolvedViewerSources({
    jobId,
    viewerSources: viewerSources as ViewerSource[] | undefined,
    orderedViewerSources: computed.orderedViewerSources,
    selectedPlanDocumentId,
    setSelectedPlanDocumentId,
  });
  const status = useJobStatus(details?.job);
  const pdfExport = details?.pdfExport;
  const exportStatus = toProcessingStateWithIdle(pdfExport?.status);
  const pdfExportOptimisticStartStatusRef = useRef<ProcessingStateWithIdle>("idle");
  const isPdfExportServerInProgress =
    exportStatus === GLOBAL_PROCESSING_PENDING ||
    exportStatus === GLOBAL_PROCESSING_PROCESSING;
  const isPdfExportInProgress =
    isQueueingPdfExport || isPdfExportOptimisticPending || isPdfExportServerInProgress;
  const exportStatusForUi =
    isPdfExportOptimisticPending && !isPdfExportServerInProgress
      ? GLOBAL_PROCESSING_PENDING
      : exportStatus;
  const canExportPdf = status.isResults && !isPdfExportInProgress;
  const canDownloadPdf =
    !isPdfExportInProgress &&
    Boolean(pdfExportDownloadUrl);

  useEffect(() => {
    const currentJobId = details?.job._id ? String(details.job._id) : "";
    const previousContext = pdfExportJobContextRef.current;
    if (
      !currentJobId ||
      (previousContext?.jobId === currentJobId && previousContext.hasSchedule === hasSchedule)
    ) {
      return;
    }

    pdfExportJobContextRef.current = { jobId: currentJobId, hasSchedule };
    setIncludeScheduleInPdf(hasSchedule);
  }, [details?.job._id, hasSchedule]);

  useEffect(() => {
    if (optimisticScheduleHeaders.length === 0) {
      return;
    }

    const persistedHeaders = new Set(overrideHeaders);
    setOptimisticScheduleHeaders((previous) => {
      const next = previous.filter((header) => !persistedHeaders.has(header));
      return next.length === previous.length ? previous : next;
    });
  }, [optimisticScheduleHeaders.length, overrideHeaders]);

  useEffect(() => {
    const validTagIds = new Set(tags.map((tag) => String(tag._id)));
    setExpandedBreakdownTagRowIds((previous) => {
      const next = new Set(Array.from(previous).filter((tagId) => validTagIds.has(tagId)));
      return next.size === previous.size ? previous : next;
    });
  }, [tags]);

  const handleToggleExpandedBreakdownTagRow = useCallback((tagId: string) => {
    setExpandedBreakdownTagRowIds((previous) => {
      const next = new Set(previous);
      if (next.has(tagId)) {
        next.delete(tagId);
      } else {
        next.add(tagId);
      }
      return next;
    });
  }, []);

  useEffect(() => {
    setPdfExportIncludeColumns((previous) => {
      const next = previous.filter((column) => availablePdfExportColumns.has(column));
      return next.length === previous.length && next.every((column, index) => column === previous[index])
        ? previous
        : next;
    });
  }, [availablePdfExportColumns]);

  const handleSelectPdfExportColumn = useCallback((column: string) => {
    setPdfExportIncludeColumns((previous) =>
      previous.includes(column) ? previous : [...previous, column]
    );
  }, []);

  const handleRemovePdfExportColumn = useCallback((column: string) => {
    setPdfExportIncludeColumns((previous) => previous.filter((candidate) => candidate !== column));
  }, []);

  const handleSelectAllPdfExportColumns = useCallback(
    (sectionId: string) => {
      const values =
        sectionId === "takeoff-columns"
          ? [...PDF_EXPORT_MEASUREMENT_COLUMNS]
          : availableScheduleColumns.map((column) => encodePdfExportScheduleColumn(column.value));
      setPdfExportIncludeColumns((previous) =>
        Array.from(new Set([...previous, ...values.filter((value) => availablePdfExportColumns.has(value))]))
      );
    },
    [availablePdfExportColumns, availableScheduleColumns]
  );

  const handleDeselectAllPdfExportColumns = useCallback(
    (sectionId: string) => {
      const values = new Set(
        sectionId === "takeoff-columns"
          ? [...PDF_EXPORT_MEASUREMENT_COLUMNS]
          : availableScheduleColumns.map((column) => encodePdfExportScheduleColumn(column.value))
      );
      setPdfExportIncludeColumns((previous) => previous.filter((column) => !values.has(column)));
    },
    [availableScheduleColumns]
  );

  useTakeoffSelectionRecount({
    detailsLoaded: !!details,
    showWorkspace: status.showWorkspace,
    selectedTagSignature: computed.selectedTagSignature,
    isCounting: status.isCounting,
    isSelecting: status.isSelecting,
    jobId,
    queuePlanAnalysis,
  });

  const activeSource =
    resolvedViewerSources.find(
      (source) => source.sourceId === selectedPlanDocumentId
    ) ?? resolvedViewerSources[0];

  const pageState = renderTakeoffPageState({
    jobId,
    detailsError,
    isPending,
    details,
    status,
    countsError,
    hasMeasurementTotalsError,
    measurementTotalsError,
  });
  const handleDownloadWorkbook = () => {
    const error = downloadTakeoffWorkbook({
      details,
      isCountsPending,
      countRows,
      isMeasurementTotalsInitialLoad,
      hasMeasurementTotalsError,
      selectedTagsForExport: computed.selectedTagsForExport,
      selectedResults: computed.selectedResults,
    });
    if (error) {
      toast.error(error);
    }
  };

  const handleDownloadPdf = async () => {
    if (!jobId || !canDownloadPdf) {
      return;
    }

    try {
      const { data: refreshedUrl } = await refetchPdfExportDownloadUrl();
      const downloadUrl = refreshedUrl ?? pdfExportDownloadUrl;
      if (!downloadUrl) {
        throw new Error("Failed to get download URL");
      }

      const response = await fetch(downloadUrl);
      if (!response.ok) {
        throw new Error("Failed to fetch file");
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const safeJobName =
        (details?.job?.name ?? "takeoff")
          .trim()
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, "-")
          .replace(/^-+|-+$/g, "") || "takeoff";
      a.href = url;
      a.download = `${safeJobName}-takeoff-export.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Download started");
    } catch (error) {
      toast.error("Failed to download PDF export");
      console.error(error);
    }
  };

  const handleQueuePdfExport = async () => {
    if (!jobId || isPdfExportInProgress) return;

    pdfExportOptimisticStartStatusRef.current = exportStatus;
    setIsPdfExportOptimisticPending(true);
    setIsQueueingPdfExport(true);
    try {
      await generatePdfExport({
        jobId,
        includeSchedule: hasSchedule && includeScheduleInPdf,
        includeTags: includeTagsInPdf,
        includeMeasurements: includeMeasurementsInPdf,
        includeColumns: pdfExportIncludeColumns,
        reportMode: pdfReportMode,
      });
      setIsPdfExportDialogOpen(false);
      toast.success("Preparing PDF export");
    } catch (error) {
      setIsPdfExportOptimisticPending(false);
      toast.error("Failed to queue PDF export");
      console.error(error);
    } finally {
      setIsQueueingPdfExport(false);
    }
  };

  const handleSelectTag = async (tagId: string) => {
    try {
      await setIncluded({ tagId: tagId as Id<"takeoffPilotExtractedTags">, included: true });
    } catch (error) {
      toast.error("Failed to add tag");
      console.error(error);
    }
  };

  const handleRemoveTag = async (tagId: string) => {
    try {
      await setIncluded({ tagId: tagId as Id<"takeoffPilotExtractedTags">, included: false });
    } catch (error) {
      toast.error("Failed to remove tag");
      console.error(error);
    }
  };

  const handleCreateCustomLineItem = async (args: { rawValue: string; unitCost: number | null }) => {
    if (!jobId) {
      return;
    }

    try {
      await createCustomTag({
        jobId,
        rawValue: args.rawValue,
        unitCost: args.unitCost,
      });
    } catch (error) {
      toast.error("Failed to create custom line item");
      console.error(error);
      throw error;
    }
  };

  const handleSearchDocumentTags = async (query: string): Promise<DocumentTagSearchResponse> => {
    if (!jobId) {
      return { status: "no_match", matches: [] };
    }

    return await convex.query(api.takeoffPilot.tags.searchDocumentTagsForCustom, {
      jobId,
      query,
    });
  };

  const handleCreateCustomLineItemsFromDocumentTags = async (
    documentTagIds: Id<"documentIntelligenceCanonicalTags">[]
  ) => {
    if (!jobId) {
      return;
    }

    try {
      const createdTagIds = await createCustomTagsFromDocumentTags({
        jobId,
        documentTagIds,
      });
      if (createdTagIds.length === 0) {
        toast.success("No new custom line items added");
        return;
      }
      toast.success(
        createdTagIds.length === 1
          ? "Added 1 custom line item"
          : `Added ${createdTagIds.length} custom line items`
      );
    } catch (error) {
      toast.error("Failed to add selected tags");
      console.error(error);
      throw error;
    }
  };

  const handleCreateCustomScheduleColumn = useCallback(
    async (header: string) => {
      if (!jobId) {
        return;
      }

      const trimmedHeader = header.trim();
      if (!trimmedHeader) {
        toast.error("Column name is required");
        throw new Error("Column name is required");
      }

      const normalizedHeader = trimmedHeader.toLocaleLowerCase();
      const hasDuplicateHeader = availableScheduleColumns.some(
        (column) => column.value.trim().toLocaleLowerCase() === normalizedHeader
      );
      if (hasDuplicateHeader) {
        toast.error("Column already exists");
        throw new Error("Column already exists");
      }

      const includedTagIds = tags.filter((tag) => tag.included).map((tag) => tag._id);
      if (includedTagIds.length === 0) {
        toast.error("Select at least one line item before adding a column");
        throw new Error("No included line items available for custom column");
      }

      try {
        await addScheduleOverrideColumn({
          jobId,
          tagIds: includedTagIds,
          header: trimmedHeader,
        });
        setOptimisticScheduleHeaders((previous) =>
          previous.includes(trimmedHeader) ? previous : [...previous, trimmedHeader]
        );
        handleSelectScheduleHeader(trimmedHeader);
        toast.success(`Added ${trimmedHeader} column`);
      } catch (error) {
        toast.error("Failed to add column");
        console.error(error);
        throw error;
      }
    },
    [addScheduleOverrideColumn, availableScheduleColumns, handleSelectScheduleHeader, jobId, tags]
  );

  const handleOpenScheduleRow = useCallback((args: {
    tagRawValue: string;
    matchedRow: LinkedSchedulePreviewSummary["matchedRows"][number];
  }) => {
    setSelectedScheduleRow(args);
  }, []);
  const handleNavigableTagIdsChange = useCallback((tagIds: string[]) => {
    setNavigableTagIds((current) => {
      if (current.size === tagIds.length && tagIds.every((tagId) => current.has(tagId))) {
        return current;
      }
      return new Set(tagIds);
    });
  }, []);
  const handleCloseWorkspace = useCallback(() => {
    if (returnToRevPilotComparisonId) {
      navigate({
        to: "/revPilot/$comparisonId",
        params: { comparisonId: returnToRevPilotComparisonId },
      });
      return;
    }

    let hasSameOriginReferrer = false;
    if (document.referrer.length > 0) {
      try {
        hasSameOriginReferrer = new URL(document.referrer).origin === window.location.origin;
      } catch {
        hasSameOriginReferrer = false;
      }
    }
    if (window.history.length > 1 && hasSameOriginReferrer) {
      window.history.back();
      return;
    }
    navigate({ to: "/takeoffPilot" });
  }, [navigate, returnToRevPilotComparisonId]);

  const resolvedJobId = jobId as Id<"takeoffPilotJobs">;
  useEffect(() => {
    if (!isPdfExportOptimisticPending) {
      return;
    }

    if (exportStatus === GLOBAL_PROCESSING_PENDING || exportStatus === GLOBAL_PROCESSING_PROCESSING) {
      setIsPdfExportOptimisticPending(false);
      return;
    }

    if (
      (exportStatus === GLOBAL_PROCESSING_COMPLETED ||
        exportStatus === GLOBAL_PROCESSING_FAILED) &&
      exportStatus !== pdfExportOptimisticStartStatusRef.current
    ) {
      setIsPdfExportOptimisticPending(false);
    }
  }, [isPdfExportOptimisticPending, exportStatus]);

  const previousPdfExportStatusRef = useRef<ProcessingStateWithIdle | null>(null);

  useEffect(() => {
    if (!status.isResults) {
      previousPdfExportStatusRef.current = exportStatus;
      return;
    }

    const previousStatus = previousPdfExportStatusRef.current;
    previousPdfExportStatusRef.current = exportStatus;
    if (previousStatus === null || previousStatus === exportStatus) {
      return;
    }

    if (exportStatus === GLOBAL_PROCESSING_COMPLETED) {
      toast.success("PDF export ready to download");
      return;
    }

    if (exportStatus === GLOBAL_PROCESSING_FAILED) {
      toast.error(pdfExport?.error || "PDF export failed");
    }
  }, [pdfExport?.error, exportStatus, status.isResults]);

  if (pageState) return pageState;

  // Main full-bleed workspace
  return (
    <div className="relative flex h-full flex-col overflow-hidden bg-background">
      {/* Main content - PDF viewer with overlay panel */}
      {status.showWorkspace ? (
        <div className="relative flex-1 overflow-hidden">
          {/* PDF Viewer - full size always */}
          <div className="absolute inset-0">
            {renderTakeoffResultsViewer({
              activeSource,
              jobId: resolvedJobId,
              viewerRef: takeoffResultsViewerRef,
              viewerTags: computed.viewerTags,
              onNavigableTagIdsChange: handleNavigableTagIdsChange,
              viewerSources: resolvedViewerSources,
              selectedPlanDocumentId,
              onSelectPlanDocumentId: setSelectedPlanDocumentId,
              onClose: handleCloseWorkspace,
              onDownloadWorkbook: handleDownloadWorkbook,
              onExportPdf: () => setIsPdfExportDialogOpen(true),
              onDownloadPdf: () => void handleDownloadPdf(),
              exportStatus: exportStatusForUi,
              exportError: pdfExport?.error,
              isPdfExportInProgress,
              canExportPdf,
              canDownloadPdf,
              canDownload:
                computed.selectedCount > 0 &&
                status.isResults &&
                !isCountsPending &&
                !!countRows &&
                !isMeasurementTotalsInitialLoad &&
                !hasMeasurementTotalsError,
              bottomPanelHeight: isPanelOpen ? panelHeight : 0,
            })}
          </div>

          {/* Table Panel - overlay on top */}
          {isPanelOpen && (
            <ResizableBottomPanel
              isOpen={isPanelOpen}
              height={panelHeight}
              minHeight={150}
              maxHeight={innerHeight - 100}
              onHeightChange={setPanelHeight}
              header={
                <PanelHeader
                  onCreateCustomLineItem={handleCreateCustomLineItem}
                  onSearchDocumentTags={handleSearchDocumentTags}
                  onCreateCustomLineItemsFromDocumentTags={handleCreateCustomLineItemsFromDocumentTags}
                  canManageCustomLineItems={status.isResults && !status.isCounting}
                  onCreateCustomScheduleColumn={handleCreateCustomScheduleColumn}
                  canManageCustomScheduleColumns={status.isResults && !status.isCounting}
                  tags={tags}
                  tables={tables}
                  selectedTagIds={tags.filter((t) => t.included).map((t) => t._id)}
                  onSelectTag={handleSelectTag}
                  onRemoveTag={handleRemoveTag}
                  isCounting={status.isCounting}
                  onDropdownSelect={handleDropdownSelect}
                  onDropdownRemove={handleDropdownRemove}
                  onDropdownOpenChange={handleDropdownOpenChange}
                  pendingSelections={isDropdownOpen ? pendingSelections : undefined}
                  hasSchedule={hasSchedule}
                  availableScheduleColumns={availableScheduleColumns}
                  selectedScheduleHeaders={selectedScheduleHeaders}
                  onSelectScheduleHeader={handleSelectScheduleHeader}
                  onRemoveScheduleHeader={handleRemoveScheduleHeader}
                  selectedQuantityColumns={visibleQuantityColumns}
                  onSelectQuantityColumn={handleSelectQuantityColumn}
                  onRemoveQuantityColumn={handleRemoveQuantityColumn}
                  costColumnsVisible={costColumnsVisible}
                  onCostColumnsVisibleChange={setCostColumnsVisible}
                  onHideResults={() => {
                    void handleHideResults();
                  }}
                />
              }
            >
              {isCountsPending || isMeasurementTotalsInitialLoad || !countRows ? (
                <SpinnerState className="h-full" />
              ) : (
                <TagResultsTable
                  tags={tags}
                  hasSchedule={hasSchedule}
                  selectedResults={computed.selectedResults}
                  selectedScheduleHeaders={selectedScheduleHeaders}
                  customScheduleHeaders={customScheduleHeaders}
                  visibleQuantityColumns={visibleQuantityColumnSet}
                  costColumnsVisible={costColumnsVisible}
                  linkedPreviewsByTagId={linkedPreviewsByTagId}
                  scheduleOverridesByTagId={scheduleOverridesByTagId}
                  onUpsertScheduleOverride={async (args) => {
                    await upsertScheduleOverride({ jobId: jobId!, ...args });
                  }}
                  onRemoveScheduleOverrideCells={async (args) => {
                    await removeScheduleOverrideCells({ jobId: jobId!, ...args });
                  }}
                  onResetScheduleOverrides={async (args) => {
                    await resetScheduleOverride({ jobId: jobId!, ...args });
                  }}
                  expandedBreakdownTagRowIds={expandedBreakdownTagRowIds}
                  navigableTagIds={navigableTagIds}
                  onFocusPreviousInstance={(tagId) => {
                    takeoffResultsViewerRef.current?.focusPreviousInstanceForTag(tagId);
                  }}
                  onFocusNextInstance={(tagId) => {
                    takeoffResultsViewerRef.current?.focusNextInstanceForTag(tagId);
                  }}
                  onFocusMeasurementBreakdownRow={(args) => {
                    takeoffResultsViewerRef.current?.focusMeasurementBreakdownRow(args);
                  }}
                  onToggleExpandedBreakdownTagRow={handleToggleExpandedBreakdownTagRow}
                  onOpenScheduleRow={handleOpenScheduleRow}
                  isCounting={status.isCounting}
                  setIncluded={setIncluded}
                  removeCustomTag={removeCustomTag}
                  renameCustomTag={renameCustomTag}
                  setUnitCost={setUnitCost}
                  setCostBasis={setCostBasis}
                  setColorHex={setTagColorHex}
                />
              )}
            </ResizableBottomPanel>
          )}
        </div>
      ) : null}

      {/* Floating toggle button when panel is closed */}
      <FloatingPanelToggle
        isOpen={isPanelOpen}
        onClick={() => setIsPanelOpen(true)}
        tagCount={computed.selectedCount}
      />
      <RowDetailsDialog
        open={selectedScheduleRow !== null}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedScheduleRow(null);
          }
        }}
        title={selectedScheduleRow ? `Schedule Row · ${selectedScheduleRow.tagRawValue}` : ""}
        description={
          selectedScheduleRow
            ? [
                selectedScheduleRow.matchedRow.tableName,
                selectedScheduleRow.matchedRow.pageNumber
                  ? `Page ${selectedScheduleRow.matchedRow.pageNumber}`
                  : null,
                `Match ${selectedScheduleRow.matchedRow.rowIndex + 1}`,
              ]
                .filter(Boolean)
                .join(" • ")
            : undefined
        }
        cells={selectedScheduleRow?.matchedRow.cells ?? {}}
      />
      <Dialog open={isPdfExportDialogOpen} onOpenChange={setIsPdfExportDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Export PDF options</DialogTitle>
            <DialogDescription>
              Choose which overlays and summary columns to include in your takeoff export.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="rounded-lg border bg-muted/20 p-3">
              <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                Report Mode
              </p>
              <Select value={pdfReportMode} onValueChange={(value) => setPdfReportMode(value as "executive_summary" | "full")}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="full">Full - include measurement detail</SelectItem>
                  <SelectItem value="executive_summary">Executive Summary - summary rows only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="rounded-lg border bg-muted/20 p-3">
              <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                Plan Content
              </p>
              <div className="space-y-3">
                {hasSchedule ? (
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={includeScheduleInPdf}
                      onCheckedChange={(checked) => setIncludeScheduleInPdf(checked === true)}
                    />
                    Include schedule pages
                  </label>
                ) : null}
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={includeTagsInPdf}
                    onCheckedChange={(checked) => setIncludeTagsInPdf(checked === true)}
                  />
                  Include tag callouts on plans
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={includeMeasurementsInPdf}
                    onCheckedChange={(checked) => setIncludeMeasurementsInPdf(checked === true)}
                  />
                  Include measurement markup on plans
                </label>
              </div>
            </div>
            <div className="rounded-lg border bg-muted/20 p-3">
              <p className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                Breakdown Columns
              </p>
              <MultiSelect
                sections={pdfExportColumnSections}
                selected={pdfExportIncludeColumns}
                onSelect={handleSelectPdfExportColumn}
                onRemove={handleRemovePdfExportColumn}
                onSelectAll={handleSelectAllPdfExportColumns}
                onDeselectAll={handleDeselectAllPdfExportColumns}
                placeholder="Include columns..."
                triggerWidth="w-full"
                dropdownWidth="w-[42rem] max-w-[calc(100vw-4rem)]"
                columns="auto-fit"
                optionMinWidth="10rem"
                showSelectedBadges={false}
                wrapOptionLabels
              />
              <p className="mt-3 text-xs text-muted-foreground">
                {pdfExportIncludeColumns.length === 0
                  ? "No breakdown columns selected. The export will include the cover page and drawing pages only."
                  : `${pdfExportIncludeColumns.length} breakdown column${pdfExportIncludeColumns.length === 1 ? " is" : "s are"} selected.`}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              disabled={isPdfExportInProgress}
              onClick={() => setIsPdfExportDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button disabled={isPdfExportInProgress} onClick={() => void handleQueuePdfExport()}>
              {isPdfExportInProgress ? "Preparing..." : "Export PDF"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
